import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { BASE_URL } from "../../../../../BaseUrl";
import {
  _t,
  customStyle,
  getCookie,
} from "../../../../../functions/Functions";
import { ToastError, ToastSuccess } from "../../../../../functions/ToastShow";
import { SettingsContext } from "../../../../../contexts/Settings";
import { getLocationConfig, getLocationOptionId } from "./LocationSetupConfig";

const EMPTY_FORM = {
  name: "",
  parent_id: null,
  parentOption: null,
  status: 1,
};

const LocationSetupModal = ({
  show,
  type,
  editId,
  parentActiveList,
  onHide,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const settingsContext = useContext(SettingsContext);
  const fetchCoverageList =
    settingsContext && settingsContext.fetchCoverageList;

  const config = useMemo(() => getLocationConfig(type), [type]);
  const isEdit = Boolean(editId);
  const onHideRef = useRef(onHide);
  const requestIdRef = useRef(0);

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [parentOptions, setParentOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    onHideRef.current = onHide;
  }, [onHide]);

  useEffect(() => {
    if (!show) {
      setLoading(false);
      setSaving(false);
      setFormData(EMPTY_FORM);
      setParentOptions([]);
      return;
    }

    if (!config) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    let cancelled = false;

    const isCurrent = () =>
      !cancelled && requestIdRef.current === requestId;

    const load = async () => {
      setLoading(true);
      setSaving(false);

      try {
        let options = [];

        if (config.parentType) {
          const cached = Array.isArray(parentActiveList) ? parentActiveList : [];

          if (cached.length) {
            options = cached;
          } else if (typeof fetchCoverageList === "function") {
            const parsed = await fetchCoverageList(config.parentType, 1);
            options =
              parsed && Array.isArray(parsed.activeList)
                ? parsed.activeList
                : [];
          }

          if (isCurrent()) setParentOptions(options || []);
        } else if (isCurrent()) {
          setParentOptions([]);
        }

        if (!editId) {
          if (!isCurrent()) return;
          if (config.parentKey && options && options.length) {
            const lastParent = options[options.length - 1];
            const lastParentId = getLocationOptionId(
              lastParent,
              config.parentOptionValueKey
            );
            setFormData({
              name: "",
              parent_id: lastParentId,
              parentOption: lastParent,
              status: 1,
            });
          } else {
            setFormData(EMPTY_FORM);
          }
          return;
        }

        const res = await axios.get(BASE_URL + config.endpoint + "/" + editId, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        });
        if (!isCurrent()) return;

        const item = res.data && res.data.data ? res.data.data : res.data;
        if (!item) {
          ToastError({ title: _t(t("Please try again")) });
          if (typeof onHideRef.current === "function") {
            onHideRef.current();
          }
          return;
        }

        if (config.parentFixed) {
          setFormData({
            name: item[config.nameKey] != null ? item[config.nameKey] : "",
            parent_id: config.parentFixed.id,
            parentOption: config.parentFixed,
            status: item.status != null ? item.status : 1,
          });
          return;
        }

        const parentId =
          config.parentKey && item[config.parentKey] != null
            ? item[config.parentKey]
            : item.country_id != null
              ? item.country_id
              : item.province_id != null
                ? item.province_id
                : null;

        const parentOption =
          parentId != null
            ? (options || []).find((opt) => {
              const optionId = getLocationOptionId(
                opt,
                config.parentOptionValueKey
              );
              return String(optionId) === String(parentId);
            }) || null
            : null;

        setFormData({
          name: item[config.nameKey] != null ? item[config.nameKey] : "",
          parent_id: parentId,
          parentOption: parentOption,
          status: item.status != null ? item.status : 1,
        });
      } catch (error) {
        if (!isCurrent()) return;
        ToastError({ title: _t(t("Please try again")) });
        if (typeof onHideRef.current === "function") {
          onHideRef.current();
        }
      } finally {
        if (isCurrent()) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, type, editId, config, t]);

  if (!config) return null;

  const setValue = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleParentChange = (option) => {
    const optionId = getLocationOptionId(
      option,
      config.parentOptionValueKey
    );
    setFormData((prev) => ({
      ...prev,
      parentOption: option || null,
      parent_id: optionId,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = formData.name ? String(formData.name).trim() : "";
    if (!name) {
      ToastError({ title: _t(t("Please enter name")) });
      return;
    }

    if (
      config.parentKey &&
      !config.parentFixed &&
      formData.parent_id == null
    ) {
      ToastError({
        title: _t(t("Please select")) + " " + _t(t(config.parentLabel)),
      });
      return;
    }

    const payload = config.buildPayload({
      name: name,
      parent_id: formData.parent_id,
      status: formData.status === 1 ? 1 : 0,
    });

    setSaving(true);
    try {
      const url = isEdit
        ? BASE_URL + config.endpoint + "/" + editId
        : BASE_URL + config.endpoint;
      const method = isEdit ? "put" : "post";
      await axios[method](url, payload, {
        headers: { Authorization: "Bearer " + getCookie() },
      });

      ToastSuccess({
        title: _t(
          t(
            isEdit
              ? config.title + " has been updated"
              : config.title + " has been added"
          )
        ),
      });
      if (typeof onSuccess === "function") {
        onSuccess(type);
      }
      if (typeof onHide === "function") onHide();
    } catch (error) {
      const message =
        error && error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : null;
      ToastError({ title: _t(t(message || "Please try again")) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      zIndex={15050}
      enforceFocus={false}
    >
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {_t(t(isEdit ? "Edit " + config.title : "Add " + config.title))}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <p className="text-center mb-0">{_t(t("Loading"))}...</p>
          ) : (
            <>
              <div className="form-group mb-3">
                <label className="form-label mb-1">
                  {_t(t(config.nameLabel))}
                  <span className="text-danger ml-1">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setValue("name", e.target.value)}
                  placeholder={_t(t("Enter " + config.title))}
                  required
                />
              </div>

              {config.parentKey ? (
                <div className="form-group mb-3">
                  <label className="form-label mb-1">
                    {_t(t(config.parentLabel))}
                    <span className="text-danger ml-1">*</span>
                  </label>
                  {config.parentFixed ? (
                    <input
                      type="text"
                      className="form-control"
                      value={config.parentFixed.name}
                      readOnly
                      disabled
                    />
                  ) : (
                    <Select
                      options={parentOptions}
                      value={formData.parentOption}
                      getOptionLabel={(option) =>
                        option[config.parentOptionLabelKey] ||
                        option.name ||
                        ""
                      }
                      getOptionValue={(option) =>
                        String(
                          getLocationOptionId(
                            option,
                            config.parentOptionValueKey
                          ) || ""
                        )
                      }
                      onChange={handleParentChange}
                      classNamePrefix="select"
                      styles={customStyle}
                      isClearable
                      placeholder={
                        _t(t("Please select")) +
                        " " +
                        _t(t(config.parentLabel)) +
                        ".."
                      }
                    />
                  )}
                </div>
              ) : null}

              {/* {config.hasStatus ? (
                <div className="form-check">
                  <input
                    id="coverageAreaStatus"
                    type="checkbox"
                    className="form-check-input ml-0"
                    checked={formData.status === 1}
                    onChange={() =>
                      setValue("status", formData.status === 1 ? 0 : 1)
                    }
                  />
                  <label
                    className="form-check-label ml-2"
                    htmlFor="coverageAreaStatus"
                  >
                    {_t(t("Active"))}
                  </label>
                </div>
              ) : null} */}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn rounded hov-btn"
            onClick={onHide}
            disabled={saving}
          >
            {_t(t("Cancel"))}
          </button>
          <button
            type="submit"
            className="btn btn-primary rounded"
            disabled={saving || loading}
          >
            {_t(t("Apply"))}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default React.memo(LocationSetupModal);